import React from 'react';
import { Card, CardContent, Stack, Typography, CircularProgress, IconButton, Box } from '@mui/material';
import { TrendingUp, MoreVert } from '@mui/icons-material';
import { MetricTile } from './types';

const RING_SIZE = 128; // unified size to better match screenshot

// Inline trend indicator (replaces chip) matching screenshot style
function TrendIndicator({ pct }: { pct?: number }) {
  if (pct == null) return null;
  const positive = pct >= 0;
  return (
    <Stack direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ color: positive ? 'success.main' : 'error.main' }}>
      <TrendingUp fontSize="inherit"
        sx={{ transform: positive ? 'none' : 'rotate(180deg)' }} />
      <Typography variant="textMD"
        fontWeight={600}>{`${positive ? '+' : ''}${pct.toFixed(1)}%`}</Typography>
    </Stack>
  );
}

// Segmented donut to mirror screenshot (supports metric.segments = [{value,color}])
function SegmentedRing({ segments }: { segments: { value: number; color: string }[] }) {
  const size = RING_SIZE;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  let offset = 0;
  return (
    <svg width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: 'rotate(-90deg)' }}>
      {/* Track */}
      <circle cx={size / 2}
        cy={size / 2}
        r={r}
        strokeWidth={stroke}
        stroke="#eee"
        fill="none" />
      {segments.map((seg, i) => {
        const pct = seg.value / total;
        const len = pct * C;
        const dashArray = `${len} ${C - len}`;
        const circle = (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={stroke}
            stroke={seg.color}
            strokeDasharray={dashArray}
            strokeDashoffset={-offset}
            strokeLinecap="butt"
            fill="none"
          />
        );
        offset += len; // advance offset
        return circle;
      })}
    </svg>
  );
}

// Single value circular percent fallback
function PercentCircle({ value, max = 1, segments }: { value: number; max?: number; segments?: { value: number; color: string }[] }) {
  if (segments && segments.length > 0) {
    return (
      <Stack alignItems="center"
        justifyContent="center"
        position="relative"
        width={RING_SIZE}
        height={RING_SIZE}
        marginRight={'16px'}>
        <SegmentedRing segments={segments} />
      </Stack>
    );
  }
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const progressColor = pct > 70 ? 'success.main' : 'warning.main';
  return (
    <Stack alignItems="center"
      justifyContent="center"
      position="relative"
      width={RING_SIZE}
      height={RING_SIZE}
      marginRight={'16px'}>
      <CircularProgress variant="determinate"
        value={100}
        size={RING_SIZE}
        thickness={6}
        sx={{ color: 'grey.200', position: 'absolute', left: 0, top: 0 }} />
      <CircularProgress variant="determinate"
        value={pct}
        size={RING_SIZE}
        thickness={6}
        sx={{ color: progressColor }} />
    </Stack>
  );
}

export function MetricCard({ metric }: { metric: MetricTile & { segments?: { value: number; color: string }[]; description?: string } }) {
  const max = metric.max ?? 1;
  const label = metric.format ? metric.format(metric.value) : `${Math.round((metric.value / max) * 100)}%`;
  return (
    <Card variant="outlined"
      sx={{ display: 'flex', borderRadius: 3 }}>
      <CardContent sx={{ p: 2.5, pl: 2.25, display: 'flex', alignItems: 'stretch', gap: 2.5, width: '100%' }}>
        <PercentCircle value={metric.value}
          max={max}
          segments={metric.segments} />
        <Box flex={1}
          display="flex"
          flexDirection="column"
          justifyContent={'space-between'}
          minWidth={0}>
          <Box display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={1}>
            <Typography variant="textMD"
              fontWeight={700}
              color="text.primary"
              noWrap>{metric.label}</Typography>
            <IconButton size="small"
              edge="end"
              sx={{ mt: -0.5, mr: -0.5 }}
              aria-label="more options">
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
          {metric.description && (
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ mt: 0.25, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {metric.description}
            </Typography>
          )}
          <Box mt={1.25}
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}>
            <Typography component="h5"
              fontWeight={700}
              lineHeight={1}>{label}</Typography>
            <TrendIndicator pct={metric.trendPct} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
